/* global document window */
import lodashMerge from 'lodash/merge';
import lodashIsFinite from 'lodash/isFinite';

import {assertDependencies, constrainAngle360, waitUntilTruthy} from '../lib/util';
import SpinnerHotspot from '../lib/spinner';
import {generateHotspotImage} from '../lib/image_util';

import Hotspot from './models/Hotspot';
import SceneInfo from './models/SceneInfo';
import GazeInfo from './models/GazeInfo';
import MouseInfo from './models/MouseInfo';

const KRPANO_ID = 'krpanoSWFObject';
const HOTSPOT_ID_PROPERTY = 'style';

const MANIFEST = require('./manifest');

const DEFAULT_OPTIONS = {
	// How long to cache gathered hotspots, for performance reasons
	hotspots_cache_ms: 350,

	// Reduce the frequency with which we query DOM to at most this
	kr_pano_cache_ttl: 500,

	// Only finish initialization once the scene is loaded
	wait_for_scene_to_load: true,

	hotspot_id_format_prefix: 'IconTRACK_',

	hotspot_id_normalizer: null,
};

function KrPanoClient(options = DEFAULT_OPTIONS, deps = MANIFEST.dependencies) {
	const thisClient = this;

	options = lodashMerge({}, DEFAULT_OPTIONS, options);
	assertDependencies(MANIFEST.name, MANIFEST.dependencies, deps);

	const log = deps.logger.child(MANIFEST.name);

	let _cachedCanvas = null;
	let _cachedKrPano = null;
	let _cachedKrPanoInvalid = true;
	let _hotspotsCache = null;
	let _hotspotsCacheTimestamp = null;

	Object.assign(thisClient, /** @lends KrPanoClient.prototype */ {
		init,
		getKRPano,
		isReady,
		isInVrMode,
		getHotspots,
		getActionContent,
		setActionContent,
		getCurrentSceneName,
		getSceneInfo,
		getMouseInfo,
		getLanguage,
		getGazeInfo,
		getVersion,
		goToScene,
		addSpinner,
		setTooltip,
		generateImage,
	});

	function init(callback) {
		return waitUntilTruthy(getKRPano, 10000, (err, krPano) => {
			if (err) {
				err = new Error(`Couldn't locate #${KRPANO_ID}. Is this a krpano tour?`);
				log.error(err);

				// try that again
				return waitUntilTruthy(getKRPano, 10000, (err, krPano) => {
					if (err) {
						err = new Error(`FATAL: Couldn't locate #${KRPANO_ID}. Is this a krpano tour?`);
						log.error(err);
						return callback && callback(err);
					}
					return gotKrPano(krPano);
				});
			}

			return gotKrPano(krPano);
		});

		function gotKrPano(krPano) {
			if (typeof window !== 'undefined') {
				window.krglobal = krPano;
			}
			if (!options.wait_for_scene_to_load) {
				return initDone();
			}

			return waitUntilTruthy(getCurrentSceneName, 20000, (err) => {
				if (err) {
					err = new Error(`Scene not loaded after 20 seconds. Tour broken?`);
					log.error(err);
					return callback && callback(err);
				}

				return initDone();
			});

			function initDone() {
				log.verbose('Initialized');
				return callback && callback(null, krPano);
			}
		};
	}

	function isReady() {
		return !!getKRPano();
	}

	function getKRPano() {
		if (_cachedKrPano && !_cachedKrPanoInvalid) {
			return _cachedKrPano;
		}

		const el = document.getElementById(KRPANO_ID);
		let kp;
		if (el && el.get) {
			kp = el.get('global');
		}
		if (!kp) {
			kp = el;
		}

		if (kp && options.kr_pano_cache_ttl) {
			_cachedKrPano = kp;
			_cachedKrPanoInvalid = false;
			setTimeout(invalidateCachedKRPano, options.kr_pano_cache_ttl);
		}

		// During transitions between VR and web mode, we prefer returning ANY krPano that we have, instead of causing errors
		return kp || _cachedKrPano;
	}

	function getKrPanoOrDie() {
		const kp = getKRPano();
		if (!kp) {
			throw new KrPanoNotReadyError();
		}
		return kp;
	}

	function invalidateCachedKRPano() {
		_cachedKrPanoInvalid = true;
	}

	/**
	 * @returns {Hotspot[]}
	 */
	function getHotspots() {
		const kp = getKrPanoOrDie();

		const now = new Date();
		if (options.hotspots_cache_ms && _hotspotsCache && now - _hotspotsCacheTimestamp < options.hotspots_cache_ms) {
			return _hotspotsCache;
		}

		_hotspotsCache = collectHotspots(kp, options.hotspot_id_normalizer || options.hotspot_id_format_prefix);
		_hotspotsCacheTimestamp = new Date();
		return _hotspotsCache;
	}

	/**
	 * Return krpano action content (code inside action)
	 * @param name
	 * @returns {string}
	 */
	function getActionContent(name) {
		return getKrPanoOrDie().get(`action[${name}].content`);
	}

	/**
	 * Set krpano action content (code inside action).
	 * @param name
	 * @param content
	 */
	function setActionContent(name, content) {
		return getKrPanoOrDie().set(`action[${name}].content`, content);
	}

	function getCurrentSceneName() {
		return getKrPanoOrDie().get('xml.scene');
	}

	/**
	 * @param sceneName Scene name to get info of. Defaults to current scene
	 * @return {SceneInfo}
	 */
	function getSceneInfo(sceneName = null) {
		const kp = getKrPanoOrDie();

		if (!sceneName) {
			sceneName = getCurrentSceneName();
		}

		if (!sceneName) {
			return null;
		}

		const scene = kp.get('scene[' + sceneName + ']');
		const titleOb = kp.get('data[' + getLanguage() + '_' + scene.titleid + ']')
			|| kp.get('data[' + scene.titleid + ']');
		const title = titleOb ? titleOb.content : null;

		const result = new SceneInfo();
		result.name = scene.name;
		result.title = title;
		return result;
	}

	function getLanguage() {
		return getKrPanoOrDie().get('tour_language');
	}

	/**
	 * @return {GazeInfo}
	 */
	function getGazeInfo() {
		const kp = getKrPanoOrDie();
		const gaze = new GazeInfo();
		gaze.x = constrainAngle360(kp.get('view.hlookat'));
		gaze.y = Number(kp.get('view.vlookat'));
		gaze.fov = Number(kp.get('view.fov'));
		return gaze;
	}

	/**
	 * @return {MouseInfo}
	 */
	function getMouseInfo() {
		const kp = getKrPanoOrDie();
		const mouseInfo = new MouseInfo();
		mouseInfo.x = kp.get('mouse.x');
		mouseInfo.y = kp.get('mouse.y');
		const sphereXY = kp.screentosphere(mouseInfo.x, mouseInfo.y);
		mouseInfo.sphere_x = sphereXY.x;
		mouseInfo.sphere_y = sphereXY.y;
		return mouseInfo;
	}

	function getVersion() {
		return getKrPanoOrDie().get('version');
	}

	function isInVrMode() {
		const kp = getKrPanoOrDie();

		// NOTE: This will return false when user has entered VR version of tour, but hasn't yet clicked to start the tour.
		// We are collapsing it to false. Do something different here?

		return !!(kp.get('webvr.isenabled'));
	}

	function goToScene(sceneName) {
		const kp = getKrPanoOrDie();
		kp.call(`loadscene("${sceneName}")`);
	}

	function addSpinner(name, options) {
		const kp = getKrPanoOrDie();
		const hotspot = new SpinnerHotspot(kp, name, options);
		if (options.tooltip) {
			setTooltip(hotspot, options.tooltip);
		}
		return hotspot;
	}

	function setTooltip(hotspot, tooltip) {
		const krpano = getKrPanoOrDie();
		if (typeof hotspot === 'string') {
			hotspot = krpano.get('hotspot[' + hotspot + ']');
		}

		const name = hotspot.name;

		let styles = krpano.style.getArray();
		let style = styles.find(s => {
			const sname = s.name.toLowerCase();
			return sname.includes('icondefaultpointspotstyle');
		});

		const tooltipName = name + '_tooltip';
		const tooltipId = krpano.tour_language + '_' + tooltipName;

		let dataEntry = krpano.data.getItem(tooltipId);
		if (!dataEntry) {
			dataEntry = krpano.data.createItem(tooltipId, {
				content: tooltip,
			});
		} else {
			dataEntry.content = tooltip;
		}
		krpano.set('hotspot[' + name + '].tooltip', tooltipName);

		if (style) {
			hotspot.loadstyle(style.name);
		}
		return hotspot;
	}

	function generateImage(top, mid, bottom, size = 200) {
		let canvas = _cachedCanvas;
		if (!canvas) {
			canvas = _cachedCanvas = document.createElement('canvas');
			canvas.style.display = 'none';
		}

		canvas.width = size;
		canvas.height = size;
		return generateHotspotImage(canvas, top, mid, bottom, size);
	}
}

// *********************************************************************************************************************

function collectHotspots(krPano, idPrefixOrNormalize) {
	const viewportRect = {
		left: 0,
		right: Math.max(document.documentElement.clientWidth, window.innerWidth || 0),
		top: 0,
		bottom: Math.max(document.documentElement.clientHeight, window.innerHeight || 0)
	};

	const focusRect = {
		left: viewportRect.right / 4,
		right: 3 * viewportRect.right / 4,
		top: viewportRect.bottom / 4,
		bottom: 3 * viewportRect.bottom / 4
	};

	const scene = krPano.get('xml.scene');
	let rawHotspots = null;

	try {
		rawHotspots = krPano.get('hotspot').getArray();
	}
	catch (_) {
		// Flash limitation. Try it the safe way
		try {
			rawHotspots = getRawHotspotsSafe(krPano);
		}
		catch (err) {
			throw new KrPanoClientError(err);
		}
	}

	const hotspots = [];
	for (let i = 0; i < rawHotspots.length; i++) {
		const rawHotspot = rawHotspots[i];

		const hotspot = new Hotspot();
		let centerPoint;
		try {
			centerPoint = rawHotspot.getcenter();
		} catch (_) {
			centerPoint = {x: 0, y: 0};
		}

		const coords = krPano.spheretoscreen(centerPoint.x, centerPoint.y);
		if (typeof idPrefixOrNormalize === 'function') {
			hotspot.id = idPrefixOrNormalize(krPano, rawHotspot);
		} else {
			hotspot.id = normalizeId(rawHotspot[HOTSPOT_ID_PROPERTY], idPrefixOrNormalize) || null;
		}
		hotspot.name = rawHotspot.name;
		hotspot.scene = scene;
		hotspot.url = rawHotspot.url;
		hotspot.normalizedUrl = normalizeUrl(rawHotspot.url);
		hotspot.posterUrl = rawHotspot.posterurl;
		hotspot.normalizedPosterUrl = normalizeUrl(rawHotspot.posterurl);
		hotspot.x = coords.x;
		hotspot.y = coords.y;
		hotspot.z = coords.z;
		hotspot.dx = intOrNull(parseInt(hotspot.x, 10) - viewportRect.right / 2);
		hotspot.dy = intOrNull(parseInt(hotspot.y, 10) - viewportRect.bottom / 2);
		hotspot.distance = hotspot.dx !== null && hotspot.dy !== null
			? intOrNull(Math.sqrt(Math.pow(hotspot.dx, 2) + Math.pow(hotspot.dy, 2)))
			: null;
		hotspot.location = isInRect(hotspot.x, hotspot.y, focusRect)
			? Hotspot.LOCATIONS.in_focus
			: isInRect(hotspot.x, hotspot.y, viewportRect)
				? Hotspot.LOCATIONS.on_edge
				: Hotspot.LOCATIONS.off_screen;
		hotspot.on_click = rawHotspot.onclick;
		hotspot._raw = rawHotspot;

		hotspots.push(hotspot);
	}

	return hotspots;
}

// Slower method that might work in flash
function getRawHotspotsSafe(krPano) {
	const count = krPano.get('hotspot.count');

	const results = [];
	for (let i = 0; i < count; i++) {
		results.push(new FakeKRPanoHotspot(krPano, i));
	}
	return results;
}

class FakeKRPanoHotspot {
	constructor(krPano, index) {
		const prefix = 'hotspot[' + index + ']';
		this.id = krPano.get(prefix + '.id');
		this.name = krPano.get(prefix + '.name');
		this.ath = krPano.get(prefix + '.ath');
		this.aty = krPano.get(prefix + '.aty');
		this.style = krPano.get(prefix + '.style');
		this.posterurl = krPano.get(prefix + '.posterurl');
		this.url = krPano.get(prefix + '.url');
	}

	getcenter() {
		return {
			x: this.ath,
			y: this.aty
		};
	}
}

function normalizeId(rawId, idPrefix) {
	if (!rawId) {
		return null;
	}
	if (rawId.indexOf(idPrefix) < 0) {
		return null;
	}
	return rawId.slice(idPrefix.length).replace(/_/g, ' ');
}

function normalizeUrl(url) {
	if (!url) {
		return null;
	}
	const from = url.indexOf('%FIRSTXML%');
	if (from >= 0) {
		return url.slice(from + 11);
	}
	return url;
}

function isInRect(x, y, rect) {
	if (x === null || y === null) {
		return false;
	}
	return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

function intOrNull(num) {
	num = parseInt(num, 10);
	return lodashIsFinite(num) ? num : null;
}

// *********************************************************************************************************************

class KrPanoClientError extends Error {}

class KrPanoNotReadyError extends KrPanoClientError {
	constructor() {
		super(`KrPano is not yet ready`);
	}
}

// *********************************************************************************************************************

export default KrPanoClient;
